import { debounce } from 'debounce';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { goToLogin } from '../../../api/auth';
import { SearchBar } from '../../../components/Search/SearchBar/search-bar';
import { ScriptService } from '../../../controllers/ScriptService';
import i18n from '../../../i18n';
import closeIcon from '../../../images/close.png';
import hamburgerIcon from '../../../images/hamburger.png';
import { RootState } from '../../../interfaces';
import { getImageByPath } from '../../../queries/ats/common';
import { useRootMenu } from '../../../queries/ats/layout/queries';
import { Layout } from '../../../queries/ats/layout/types';
import { classes } from '../../../utils/commonUtils';
import { MenuLink } from './menu-link';
import SideMenu from './SideMenu';
import './style.scss';
import styles from './topLink.module.scss';
import UserMenu from './UserMenu';

interface LeftMenuProps {
  openSideMenu: boolean;
  setOpenSideMenu: Dispatch<SetStateAction<boolean>>;
}

interface RightMenuProps {
  shouldShowSearchDropdown: boolean;
  toggleSearchBar: () => void;
  rootMenu?: Layout.SectionItem[];
}

export default function TopMenu() {
  const [hideSearchBar, setHideSearchBar] = useState<boolean>(true);
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false);
  const [viewPortWidth, setViewPortWidth] = useState<number>(window.innerWidth);
  const searchBarWrapperClass = 'searchbar-wrapper' + (hideSearchBar ? '' : ' show');

  const onResize = debounce(() => {
    if (viewPortWidth !== window.innerWidth) {
      setViewPortWidth(window.innerWidth);
      if (!(hideSearchBar && shouldShowSearchDropdown())) {
        toggleSearchBar();
      }
    }
  }, 1000);

  useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  function toggleSearchBar() {
    setHideSearchBar(!hideSearchBar);
    setOpenSideMenu(false);
  }

  function shouldShowSearchDropdown() {
    return viewPortWidth < 560 && !ScriptService._isIE();
  }

  const { data: rootMenu } = useRootMenu();

  return (
    <>
      <LeftMenu openSideMenu={openSideMenu} setOpenSideMenu={setOpenSideMenu} />
      <CenterMenu rootMenu={rootMenu} />
      <RightMenu
        rootMenu={rootMenu}
        shouldShowSearchDropdown={shouldShowSearchDropdown()}
        toggleSearchBar={toggleSearchBar}
      />
      {shouldShowSearchDropdown() ? <div className={searchBarWrapperClass}>{<SearchBar />}</div> : null}
    </>
  );
}

function RightMenu({ rootMenu, shouldShowSearchDropdown, toggleSearchBar }: RightMenuProps) {
  const loggedInWithCredentials = useSelector((root: RootState) => root.authReducer.loggedInWithCredentials);
  const isInternetOnly = useSelector((root: RootState) => root.authReducer.isInternetOnly);
  const isInternetOnlyAuthByCreds = useSelector((root: RootState) => root.authReducer.isInternetOnlyAuthByCreds);
  const isInternetOnlyAuthByIp = useSelector((root: RootState) => root.authReducer.isInternetOnlyAuthByIp);
  const userEmail = useSelector((root: RootState) => root.authReducer.auth?.currentuser);
  const userFullName = useSelector((root: RootState) => root.authReducer.userFullName);
  const loggedInByIp = useSelector(
    (root: RootState) => root.authReducer.isHome && !root.authReducer.loggedInWithCredentials,
  );

  function SearchElement() {
    if (!shouldShowSearchDropdown) {
      return <SearchBar />;
    } else {
      return (
        <SearchButton
          onClick={() => {
            toggleSearchBar();
          }}
        />
      );
    }
  }

  function SearchButton({ ...rest }: React.HTMLAttributes<HTMLDivElement>) {
    return (
      <div className="search-button-container" {...rest}>
        <span className="searchIcon">E</span>
      </div>
    );
  }

  function LoginButton() {
    return (
      <button
        className="loginButton"
        onClick={() => {
          goToLogin();
        }}
        style={loggedInByIp ? { width: '100%' } : {}}
      >
        {i18n.t<string>('log on')} {loggedInByIp ? i18n.t<string>('with password') : ''}
      </button>
    );
  }

  return (
    <div className={'header-right'}>
      <SearchElement />
      {
        // Workaround for the Recordings menu item to be on the left side of the search bar
        // Requested by QA to follow the design of the UX team
        rootMenu?.map((item, idx) => {
          if (item.name === 'Recordings') {
            return (
              <div key={item.slug} className="top-left-recordings">
                <nav className="top">
                  <TopLink
                    key={item.slug}
                    item={item}
                    className={classes(priorityTopMenusClass(idx), styles.topLink)}
                  />
                </nav>
              </div>
            );
          }
          return null;
        })
      }
      <nav className="top">
        {loggedInWithCredentials || loggedInByIp || isInternetOnly ? (
          <div className="user-menu-container">
            <UserMenu
              isInternetOnly={isInternetOnly}
              isInternetOnlyAuthByCreds={isInternetOnlyAuthByCreds}
              isInternetOnlyAuthByIp={isInternetOnlyAuthByIp}
              loggedInByIp={loggedInByIp}
              loggedInWithCredentials={loggedInWithCredentials}
              userEmail={userEmail ?? ''}
              userFullName={userFullName ?? ''}
              renderLoginButton={LoginButton}
            />
          </div>
        ) : (
          <LoginButton />
        )}
      </nav>
    </div>
  );
}

function CenterMenu({ rootMenu }: { rootMenu?: Layout.SectionItem[] }) {
  return (
    <div className="header-center">
      <nav className="top">
        {rootMenu?.map((item, idx) => {
          if (item.name === 'Recordings') {
            // Avoid duplicate Recordings menu item
            return null;
          }
          return (
            <TopLink key={item.slug} item={item} className={classes(priorityTopMenusClass(idx), styles.topLink)} />
          );
        })}
      </nav>
    </div>
  );
}

function TopLink({ item, className }: { item: Layout.SectionItem; className?: string }) {
  const location = useLocation();

  const slug = item.isRoot ? '' : item.slug;

  const selectedClass = useMemo(() => {
    return location.pathname.indexOf(slug) !== -1 ? ' current' : '';
  }, [location.pathname, slug]);

  return (
    <MenuLink slug={slug} type={item.type} className={className + selectedClass}>
      {item.image && item.isRoot ? (
        <img crossOrigin="" src={getImageByPath(item.image, 'wide')} alt={item.name} />
      ) : (
        <span>{item.name}</span>
      )}
    </MenuLink>
  );
}

function LeftMenu({ openSideMenu, setOpenSideMenu }: LeftMenuProps) {
  const toggleClass = 'sticky-side-toggle noselect ' + (openSideMenu ? 'open' : 'closed');
  if (ScriptService.onPhone()) {
    returnToScrollPosition(openSideMenu);
  }

  return (
    <div className="header-left">
      <div
        className={toggleClass}
        onClick={() => {
          setOpenSideMenu(!openSideMenu);
        }}
      >
        {openSideMenu ? (
          <img crossOrigin="" alt={i18n.t<string>('close side menu')} src={closeIcon} />
        ) : (
          <img crossOrigin="" alt={i18n.t<string>('open side menu')} src={hamburgerIcon} />
        )}
      </div>
      <SideMenu openSideMenu={openSideMenu} setOpenSideMenu={setOpenSideMenu} />
    </div>
  );
}

function returnToScrollPosition(openSideMenu: boolean) {
  if (openSideMenu) {
    document.body.style.top = `-${window.scrollY}px`;
  } else {
    const scrollY = document.body.style.top;
    if (scrollY) {
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
    }
  }
}

function priorityTopMenusClass(idx: number) {
  let priority = '';

  switch (idx) {
    case 0:
      priority = 'sticky-home';
      break;
    case 1:
      priority = 'sticky-medium';
      break;
    case 2:
      priority = 'sticky-high';
      break;
    case 3:
      priority = 'sticky-medium';
      break;
    case 4:
      priority = 'sticky-low';
      break;
    default:
      priority = 'sticky-non-priority';
      break;
  }

  return priority;
}
